import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

const SubscriptionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentPlanPrice, setCurrentPlanPrice] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradeDetails, setUpgradeDetails] = useState(null);
  const [loadingUpgradeDetails, setLoadingUpgradeDetails] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Fetch the user's current subscription
  useEffect(() => {
    async function fetchSubscription() {
      try {
        const response = await api.getUserSubscription();
        if (!response.hasSubscription) {
          setCurrentPlan(null);
          setIsActive(false);
          setCurrentPlanPrice(null);
          enqueueSnackbar(
            <FormattedMessage
              id="subscription.noPlan"
              defaultMessage="Welcome! You haven't subscribed to a plan yet. Choose a plan to get started!"
            />,
            {
              variant: 'info',
              autoHideDuration: 5000,
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
              action: (key) => (
                <IconButton size="small" onClick={() => closeSnackbar(key)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ),
            }
          );
          return;
        }
        setCurrentPlan(response.data.plan_name);
        setIsActive(response.data.is_active);
        setSubscriptionEnd(new Date(response.data.expiry_date).toLocaleDateString());
      } catch (error) {
        console.error('Error fetching subscription:', error);
        enqueueSnackbar(
          <FormattedMessage
            id="subscription.error"
            values={{ action: 'fetch subscription' }}
            defaultMessage="Failed to {action}. Please try again later."
          />,
          {
            variant: 'error',
            autoHideDuration: 20000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            action: (key) => (
              <IconButton size="small" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }
        );
      }
    }
    fetchSubscription();
  }, []);

  // Fetch available subscription plans and set current plan's price
  useEffect(() => {
    async function fetchSubscriptionPlans() {
      try {
        setLoading(true);
        const plans = await api.getSubscriptionPlans();
        const transformedPlans = plans.map((plan) => {
          const defaultPrice = plan.prices[0] || {};
          return {
            ...plan,
            price_id: defaultPrice.id,
            price: defaultPrice.amount,
            currency: defaultPrice.currency || 'usd',
            billing_interval: defaultPrice.billing_interval || 'month',
            buttonText:
              plan.name === 'Basic'
                ? 'Start with Basic'
                : plan.name === 'Standard'
                  ? 'Upgrade to Standard'
                  : 'Upgrade to Premium',
            highlight: plan.name === 'Standard',
          };
        });
        setSubscriptionPlans(transformedPlans);

        // Find the current plan's price if the user has a subscription
        if (currentPlan) {
          const currentPlanData = transformedPlans.find((plan) => plan.name === currentPlan);
          if (currentPlanData) {
            setCurrentPlanPrice(parseFloat(currentPlanData.price));
          } else {
            console.warn(`Current plan "${currentPlan}" not found in subscription plans.`);
            setCurrentPlanPrice(0);
          }
        }
      } catch (error) {
        enqueueSnackbar(
          <FormattedMessage
            id="subscription.error"
            values={{ action: 'fetch subscription plans' }}
            defaultMessage="Failed to {action}. Please try again later."
          />,
          {
            variant: 'error',
            autoHideDuration: 20000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            action: (key) => (
              <IconButton size="small" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }
        );
      } finally {
        setLoading(false);
      }
    }
    fetchSubscriptionPlans();
  }, [currentPlan]);

  // Handle checkout verification after redirect from Stripe
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get('status');
    const sessionId = queryParams.get('session_id');

    if (status === 'success' && sessionId) {
      const verify = async () => {
        setVerifying(true);
        try {
          const body = { session_id: sessionId };
          const response = await api.verifyCheckoutSession(body);
          if (response.message === 'success') {
            enqueueSnackbar(
              <FormattedMessage
                id="subscription.success"
                defaultMessage="Subscription updated successfully!"
              />,
              {
                variant: 'success',
                autoHideDuration: 20000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                action: (key) => (
                  <IconButton size="small" onClick={() => closeSnackbar(key)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }
            );
            // Fetch the updated subscription after successful verification
            const subscriptionResponse = await api.getUserSubscription();
            if (subscriptionResponse.hasSubscription) {
              setCurrentPlan(subscriptionResponse.data.plan_name);
              setIsActive(subscriptionResponse.data.is_active);
              setSubscriptionEnd(new Date(subscriptionResponse.data.expiry_date).toLocaleDateString());
            }
          } else {
            throw new Error('Verification failed');
          }
        } catch (error) {
          enqueueSnackbar(
            <FormattedMessage
              id="subscription.error"
              values={{ action: <FormattedMessage id="subscription.verify" /> }}
              defaultMessage="Failed to {action}. Please try again later."
            />,
            {
              variant: 'error',
              autoHideDuration: 20000,
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
              action: (key) => (
                <IconButton size="small" onClick={() => closeSnackbar(key)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ),
            }
          );
        } finally {
          setVerifying(false);
        }
      };
      verify();
      navigate('/account/subscription', { replace: true });
    } else if (status === 'canceled') {
      enqueueSnackbar(
        <FormattedMessage
          id="subscription.canceled"
          defaultMessage="Subscription process was canceled."
        />,
        {
          variant: 'info',
          autoHideDuration: 20000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }
      );
      navigate('/account/subscription', { replace: true });
    }
  }, [location.search]);

  const pollSubscriptionUpdate = async (expectedPlanName, maxAttempts = 10, interval = 1000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await api.getUserSubscription();
        if (response.data.plan_name === expectedPlanName) {
          setCurrentPlan(response.data.plan_name);
          setIsActive(response.data.is_active);
          setSubscriptionEnd(new Date(response.data.expiry_date).toLocaleDateString());
          return true;
        }
      } catch (error) {
        console.error(`Error polling subscription (attempt ${attempt}):`, error);
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
    enqueueSnackbar(
      <FormattedMessage
        id="subscription.update_delayed"
        defaultMessage="Subscription update is taking longer than expected. Please check back later."
      />,
      {
        variant: 'warning',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        action: (key) => (
          <IconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
    return false;
  };

  const handleOpenDialog = async (plan) => {
    if (currentPlan === plan.name && isActive) {
      enqueueSnackbar(
        <FormattedMessage
          id="subscription.already_active"
          defaultMessage="You are already on this plan."
        />,
        {
          variant: 'info',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }
      );
      return;
    }

    setSelectedPlan(plan);
    setLoadingUpgradeDetails(true);
    setOpenDialog(true);

    try {
      console.log("currentPlan:", currentPlan);
      console.log("isActive:", isActive);
      console.log("plan:", plan);

      if (!currentPlan || !isActive) {
        // First-time subscription
        const startDate = new Date();
        const endDate = new Date(startDate);

        if (plan.billing_interval === 'month') {
          endDate.setMonth(startDate.getMonth() + 1);
        } else if (plan.billing_interval === 'year') {
          endDate.setFullYear(startDate.getFullYear() + 1);
        } else {
          endDate.setMonth(startDate.getMonth() + 1);
        }
        const billingCycleEnd = endDate.toLocaleDateString();
        setUpgradeDetails({
          price_difference: plan.price,
          remaining_usage: null,
          billing_cycle_end: billingCycleEnd,
          is_first_subscription: true,
        });
      } else {
        const currentUsageResponse = await api.getUserSubscriptionUsage();
        const currentUsage = currentUsageResponse.data || currentUsageResponse;

        const currentUsageMap = Array.isArray(currentUsage.features)
          ? currentUsage.features.reduce((acc, feature) => {
            acc[feature.name] = parseInt(feature.usage_count, 10) || 0;
            return acc;
          }, {})
          : {};

        const newPlanLimits = Array.isArray(plan.feature_limits)
          ? plan.feature_limits.reduce((acc, feature) => {
            const limit = parseInt(feature.limit, 10);
            if (!isNaN(limit) && feature.feature?.name) {
              acc[feature.feature.name] = limit;
            }
            return acc;
          }, {})
          : {};

        const remainingUsage = Object.keys(newPlanLimits).map((featureName) => ({
          feature: featureName,
          remaining: newPlanLimits[featureName] - (currentUsageMap[featureName] || 0),
        }));

        const newPlanPrice = parseFloat(plan.price) || 0;
        const priceDifference = currentPlanPrice !== null
          ? parseFloat((newPlanPrice - currentPlanPrice).toFixed(2))
          : newPlanPrice;

        console.log("subscriptionEnd :", subscriptionEnd);

        setUpgradeDetails({
          price_difference: priceDifference,
          remaining_usage: remainingUsage,
          billing_cycle_end: subscriptionEnd,
          is_first_subscription: false,
        });
      }
    } catch (error) {
      console.error('Error fetching upgrade details:', error);
      enqueueSnackbar(
        <FormattedMessage
          id="subscription.error"
          values={{ action: 'fetch upgrade details' }}
          defaultMessage="Failed to {action}. Please try again later."
        />,
        {
          variant: 'error',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }
      );
      setOpenDialog(false);
    } finally {
      setLoadingUpgradeDetails(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    setOpenDialog(false);
    setUpgradingPlanId(selectedPlan.id);

    try {
      const body = {
        price_id: selectedPlan.price_id,
        plan_id: selectedPlan.id,
      };
      const response = await api.subscribePlan(body);

      const checkoutUrl = response.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        enqueueSnackbar(
          <FormattedMessage
            id="subscription.success"
            defaultMessage="Subscription updated successfully!"
          />,
          {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            action: (key) => (
              <IconButton size="small" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }
        );
        await pollSubscriptionUpdate(selectedPlan.name);
      }
    } catch (error) {
      console.log('Error initiating subscription:', error.message);
      enqueueSnackbar(
        <FormattedMessage
          id="subscription.error1"
          values={{ message: error.message }}
        />,
        {
          variant: 'error',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }
      );
    } finally {
      setUpgradingPlanId(null);
      setSelectedPlan(null);
      setUpgradeDetails(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
    setUpgradeDetails(null);
  };

  if (loading || verifying) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', px: 3, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg" sx={{ px: 2, py: 4, textAlign: 'center', margin: 'auto' }}>
          <Typography variant="body1" sx={{ color: '#666' }}>
            {verifying ? 'Verifying subscription...' : 'Loading subscription plans...'}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "95vh", backgroundColor: "#f5f5f5", px: 3, display: "flex", alignItems: "center" }}>
      <Container
        maxWidth="lg"
        sx={{
          // px: 2,
          // py: 4,
          textAlign: "center",

        }}
      >

        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {(!subscriptionPlans || subscriptionPlans.length === 0) && (
            <Grid item xs={12}>
              <Box
                sx={{
                  py: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  color: "text.disabled",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  No plans available
                </Typography>
              </Box>
            </Grid>
          )}
          {subscriptionPlans?.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.name} sx={{ display: "flex" }}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  boxShadow: 3,
                  borderLeft: currentPlan === plan.name ? "4px solid #00438E" : "none",
                  backgroundColor: "#ffffff",
                  color: "#333",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  minHeight: "500px",
                  maxWidth: "390px",
                  "&:hover": { boxShadow: 6 },
                }}
              >
                {plan.highlight && (
                  <Chip
                    label={<FormattedMessage id="subscription.most_popular" />}
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontWeight: "bold",
                      backgroundColor: "#FFC107",
                      color: "#333",
                    }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: plan.highlight ? 3 : 0, color: "#00438E" }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 2, color: "#666" }}>
                    {plan.description}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: "#00438E" }}>
                    {plan.price === 0 ? "Free" : `$${plan.price}`}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mb: 3, color: "#666" }}>
                    / {plan.billing_interval}
                  </Typography>

                  <List dense sx={{ textAlign: "left", display: "inline-block" }}>
                    {plan.feature_limits.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <CheckCircleIcon sx={{ color: "#4CAF50", mr: 1 }} />
                        <ListItemText
                          primary={feature.limit ? `${feature.feature.name} - ${feature.limit}` : feature.feature.name}
                          sx={{ color: "#333" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <Box sx={{ p: 2 }}>
                  <Button
                    variant="contained"
                    disabled={currentPlan === plan.name && isActive}
                    sx={{
                      width: '100%',
                      backgroundColor: currentPlan === plan.name && isActive ? "#B0BEC5" : "#00438E",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: currentPlan === plan.name && isActive ? "#B0BEC5" : "#003A75",
                      },
                      "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" },
                    }}
                    onClick={() => handleOpenDialog(plan)}
                  >
                    {currentPlan === plan.name && isActive ? (
                      <FormattedMessage id="subscription.current_plan" defaultMessage="Current Plan" />
                    ) : upgradingPlanId === plan.id ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      plan.buttonText
                    )}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            <FormattedMessage
              id="subscription.confirm_subscription"
              defaultMessage="Confirm Subscription"
            />
          </DialogTitle>
          <DialogContent>
            {loadingUpgradeDetails ? (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <FormattedMessage
                    id="subscription.loading_details"
                    defaultMessage="Loading subscription details..."
                  />
                </Typography>
              </Box>
            ) : (
              <>
                {upgradeDetails?.is_first_subscription ? (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <FormattedMessage
                        id="subscription.first_subscription_message"
                        defaultMessage="You are about to subscribe to the {newPlan} plan."
                        values={{ newPlan: selectedPlan?.name }}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <FormattedMessage
                        id="subscription.plan_price"
                        defaultMessage="Plan Price: ${price}"
                        values={{ price: upgradeDetails?.price_difference }}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <FormattedMessage
                        id="subscription.billing_cycle_end"
                        defaultMessage="Billing cycle ends on: {endDate}"
                        values={{ endDate: upgradeDetails?.billing_cycle_end }}
                      />
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <FormattedMessage
                        id="subscription.upgrade_message"
                        defaultMessage="You are about to upgrade your plan from {currentPlan} to {newPlan}."
                        values={{ currentPlan, newPlan: selectedPlan?.name }}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      <FormattedMessage
                        id="subscription.usage_carryover"
                        defaultMessage="Usage Carryover:"
                      />
                    </Typography>
                    <List dense>
                      {upgradeDetails?.remaining_usage.map((usage, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${usage.feature}: ${usage.remaining} remaining`}
                            sx={{ color: '#333' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                      <FormattedMessage
                        id="subscription.price_difference"
                        defaultMessage="Price Difference (prorated): ${priceDifference}"
                        values={{ priceDifference: upgradeDetails?.price_difference?.toFixed(2) }}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <FormattedMessage
                        id="subscription.billing_cycle_end"
                        defaultMessage="Billing cycle ends on: {endDate}"
                        values={{ endDate: upgradeDetails?.billing_cycle_end }}
                      />
                    </Typography>
                  </>
                )}
                <Typography variant="body2" color="textSecondary">
                  <FormattedMessage
                    id="subscription.confirm_notice"
                    defaultMessage="By confirming, you agree to the charge and plan subscription."
                  />
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary">
              <FormattedMessage id="subscription.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              onClick={handleConfirmUpgrade}
              color="primary"
              variant="contained"
              disabled={loadingUpgradeDetails}
            >
              <FormattedMessage id="subscription.confirm" defaultMessage="Confirm" />
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SubscriptionPage;